import {Component} from '@angular/core';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {finalize} from 'rxjs';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {NavigationService} from '../../../core/services/navigation/navigation.service';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {AuthenticationRoutesEnum} from '../services/navigation/authentication-routes.enum';
import {Router} from '@angular/router';
import {Utils} from '@astek/utils';
import {AuthenticationResponse} from '../../../core/services/authentication/http/authentication-response';

type ExpiredPasswordErrorDetail = {
  token: string;
}

@Component({
  selector: 'xp-login',
  templateUrl: './login.component.html',
  styleUrls: ['../authentication.common.scss', './login.component.scss'],
})
export class LoginComponent {

  public readonly formGroup = this._formBuilder.group({
    login: ['', [Validators.required.bind(Validators), Validators.email.bind(Validators)]],
    password: ['', [Validators.required.bind(Validators)]],
  });

  public showPassword: boolean = false;

  private _isProcessing: boolean = false;
  private _errorKey: string = '';

  constructor(
    private readonly _router: Router,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _authenticationService: AuthenticationService,
    private readonly _navigationService: NavigationService,
  ) {
  }


  public get forgotPasswordRoute(): Array<string> {
    return ['/', RoutesEnum.AUTHENTICATION, AuthenticationRoutesEnum.FORGOT_PASSWORD];
  }

  public get signUpUrl(): string {
    // TODO_DEV redirect to the real thing
    return 'https://x-pressure.com';
  }

  public get isProcessing(): boolean {
    return this._isProcessing;
  }

  public get errorKey(): string {
    return this._errorKey;
  }

  public get emailEmpty(): boolean {
    return ((this.formGroup.controls['login'].value as string) === '');
  }

  public clearEmail(): void {
    this.formGroup.controls['login'].setValue('');
  }

  public submitLogin(): void {
    if (this.formGroup.invalid) {
      return;
    }

    this._errorKey = '';
    this._isProcessing = true;
    this._authenticationService.login(
      this.formGroup.controls['login'].value as string,
      this.formGroup.controls['password'].value as string,
    )
      .pipe(finalize(() => this._isProcessing = false))
      .subscribe({
        next: async (response: AuthenticationResponse) => {
          if (Utils.isNotNullOrUndefined(response.accessToken)) {
            return await this._navigationService.navigateTo(this._navigationService.defaultRoute);
          }
          return await this._navigationService.navigateToSubPage(
            RoutesEnum.AUTHENTICATION,
            AuthenticationRoutesEnum.TWO_FACTOR_CHECK,
          );
        },
        error: async (error: any) => {
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized as number) {
            const details: ExpiredPasswordErrorDetail = error.error as ExpiredPasswordErrorDetail;
            if (Utils.isNotBlank(details.token)) {
              await this._router.navigate(this.getChangePasswordRoute(details.token));
            } else {
              this._errorKey = 'AUTHENTICATION.LOGIN.UNAUTHORIZED';
            }
          }
        },
      });
  }

  private getChangePasswordRoute(token: string): Array<string> {
    return [[
      '/',
      RoutesEnum.AUTHENTICATION,
      AuthenticationRoutesEnum.EXPIRED_PASSWORD.replace(':token', token),
    ].join('/')];
  }
}
